import { Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { PhotoRequirement } from "@ha/data/basic";
import { LanguageStateSelectors } from "@ha/ui/common";
import { BaseValidationResult } from "@ha/ui/forms";
import { Action, State, StateContext, Store } from "@ngxs/store";
import { produce } from "immer";
import { Observable, of, tap } from "rxjs";

import { PermitStepSelectors } from "../../permit/states/permit-step.selectors";
import { PermitPhoto } from "../models/permit-photo.model";
import { PermitPhotoService } from "../services/permit-photo.service";
import { PermitPhotoActions } from "./permit-photo.actions";

export interface PermitPhotoStateModel {
    photos: PermitPhoto[];
    photoRequirement: PhotoRequirement;
    includeLinkedFlammableAtexPhotos: boolean;
}

@Injectable()
@State<PermitPhotoStateModel>({
    name: "permitPhotoplace",
    defaults: {
        photos: [],
        photoRequirement: undefined,
        includeLinkedFlammableAtexPhotos: false,
    },
})
export class PermitPhotoState {
    constructor(
        private store: Store,
        private permitPhotoService: PermitPhotoService,
        private activatedRoute: ActivatedRoute,
    ) {}

    @Action(PermitPhotoActions.GetPermitPhotos)
    public getPermitPhotos(
        ctx: StateContext<PermitPhotoStateModel>,
        action: PermitPhotoActions.GetPermitPhotos,
    ): Observable<PermitPhoto[]> {
        const permitId = this.store.selectSnapshot(PermitStepSelectors.permitId);
        if (permitId) {
            return this.permitPhotoService.getPermitPhotos(permitId, action.language, action.includeLinkedFlammableAtexPhotos).pipe(tap((result) => {
                ctx.setState(produce(ctx.getState(), (draft) => {
                    draft.photos = result;
                    draft.includeLinkedFlammableAtexPhotos = action.includeLinkedFlammableAtexPhotos;
                }));
            }));
        }

        return of();
    }

    @Action(PermitPhotoActions.GetPermitPhotoRequirement)
    public getPhotoRequirement(ctx: StateContext<PermitPhotoStateModel>): Observable<PhotoRequirement> {
        const permitId = this.store.selectSnapshot(PermitStepSelectors.permitId);

        return this.permitPhotoService.getPermitPhotoRequirement(permitId).pipe(tap((result) => {
            ctx.setState(produce(ctx.getState(), (draft) => {
                draft.photoRequirement = result;
            }));
        }));
    }

    @Action(PermitPhotoActions.SavePermitPhoto)
    public savePermitPhoto(
        ctx: StateContext<PermitPhotoStateModel>,
        action: PermitPhotoActions.SavePermitPhoto,
    ): Observable<boolean> {
        return this.permitPhotoService.savePermitPhoto(action.permitId, action.photo, action.step, action.questionId)
            .pipe(tap((result) => {
                if (result) {
                    const currentLanguage = this.store.selectSnapshot(LanguageStateSelectors.currentLanguage);
                    this.store.dispatch(new PermitPhotoActions.GetPermitPhotos(currentLanguage, ctx.getState().includeLinkedFlammableAtexPhotos));
                }
            }));
    }

    @Action(PermitPhotoActions.RemovePermitPhoto)
    public removePermitPhoto(
        ctx: StateContext<PermitPhotoStateModel>,
        action: PermitPhotoActions.RemovePermitPhoto,
    ): Observable<BaseValidationResult> {
        return this.permitPhotoService.removePhoto(action.fileName).pipe(tap((result) => {
            if (result) {
                const currentLanguage = this.store.selectSnapshot(LanguageStateSelectors.currentLanguage);
                this.store.dispatch(new PermitPhotoActions.GetPermitPhotos(currentLanguage, ctx.getState().includeLinkedFlammableAtexPhotos));
            }
        }));
    }

    @Action(PermitPhotoActions.ClearPhotos)
    public clearPhotos(ctx: StateContext<PermitPhotoStateModel>): void {
        ctx.patchState({
            photos: [],
        });
    }
}
